/**
 * baseStyles.ts - Shared base styles for automation flows components
 *
 * Purpose:
 *   Common style patterns extracted from stepNodeStyles
 *   to ensure consistency and reduce duplication.
 *
 * Usage:
 *   import { baseStyles, transitions, shadows } from '../styles/baseStyles';
 *   const myStyles = StyleSheet.create({
 *     button: { ...baseStyles.transparentButton, padding: 8 },
 *   });
 */

import { StyleSheet } from 'aphrodite';
import { brandPrimary, white, backgroundAlt, aceGray } from '../../../styles/colors';

/**
 * Transition duration constants
 */
export const TRANSITIONS = {
	FAST: '0.15s',
	NORMAL: '0.2s',
	SLOW: '0.3s',
} as const;

/**
 * Common transition strings
 */
export const transitions = {
	background: `background ${TRANSITIONS.FAST} ease`,
	opacity: `opacity ${TRANSITIONS.NORMAL} ease`,
	transform: `transform ${TRANSITIONS.FAST} ease`,
	/**
	 * Card-safe transition: avoids width/height transitions that can cause ResizeObserver churn
	 * (React Flow emits continuous `dimensions` updates while height animates).
	 */
	card: `box-shadow ${TRANSITIONS.NORMAL} ease, border-color ${TRANSITIONS.NORMAL} ease, background-color ${TRANSITIONS.NORMAL} ease`,
	all: `all ${TRANSITIONS.NORMAL} ease`,
	allSlow: `all ${TRANSITIONS.SLOW} ease`,
} as const;

/**
 * Box shadow presets
 */
export const shadows = {
	/** Subtle card shadow */
	card: '0px 4px 4px rgba(0, 0, 0, 0.08)',
	/** Elevated hover state */
	cardHover: '0 6px 16px rgba(0, 0, 0, 0.12)',
	/** Small elements like badges */
	badge: '0 2px 4px rgba(0, 0, 0, 0.15)',
	/** Floating panels */
	panel: '0 2px 8px rgba(0, 0, 0, 0.1)',
	/** Subtle depth for dots/indicators */
	dot: '0 1px 3px rgba(0,0,0,0.2)',
} as const;

/**
 * Base style objects for composition
 * These are not StyleSheet.create() outputs - use them with spread operator
 */
export const baseStyles = {
	/**
	 * Base for transparent clickable buttons
	 * Use: { ...baseStyles.transparentButton, padding: 8 }
	 */
	transparentButton: {
		background: 'transparent',
		border: 'none',
		cursor: 'pointer' as const,
		display: 'flex' as const,
		alignItems: 'center' as const,
		justifyContent: 'center' as const,
	},

	/**
	 * Focus ring style for accessibility
	 * Use: ':focus': baseStyles.focusRing
	 */
	focusRing: {
		outline: `3px solid ${brandPrimary}40`,
		outlineOffset: 2,
	},

	/**
	 * Standard divider line
	 */
	divider: {
		width: '100%',
		height: 0,
		borderTop: `1px solid ${aceGray}`,
	},

	/**
	 * Flex row with centered items
	 */
	flexRowCenter: {
		display: 'flex' as const,
		alignItems: 'center' as const,
	},

	/**
	 * Flex column
	 */
	flexColumn: {
		display: 'flex' as const,
		flexDirection: 'column' as const,
	},

	/**
	 * Dot/indicator circle base
	 */
	dotIndicator: {
		borderRadius: '50%',
		flexShrink: 0,
	},
} as const;

/**
 * Node card base style (4px border, 12px radius, shadow)
 */
export const nodeCardBase = {
	background: white,
	borderWidth: 4,
	borderStyle: 'solid' as const,
	borderRadius: 12,
	boxShadow: shadows.card,
} as const;

/**
 * Common Aphrodite styles used across components
 */
export const styleSheet = StyleSheet.create({
	/**
	 * Standard hover background for interactive items
	 */
	hoverBackground: {
		':hover': {
			background: backgroundAlt,
		},
	},

	/**
	 * Disabled/non-interactive state
	 */
	disabled: {
		opacity: 0.5,
		cursor: 'not-allowed',
		pointerEvents: 'none',
	},

	/**
	 * Grab cursor for draggable items
	 */
	draggable: {
		cursor: 'grab',
		':active': {
			cursor: 'grabbing',
		},
	},

	/**
	 * Badge/pill container
	 */
	badge: {
		borderRadius: 12,
		padding: '2px 8px',
		fontSize: 10,
		fontWeight: 600,
	},

	/**
	 * Uppercase label text
	 */
	labelUppercase: {
		textTransform: 'uppercase',
		letterSpacing: '0.5px',
	},

	/**
	 * Truncate text with ellipsis
	 */
	truncate: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});

// Backwards compatible alias
export const sharedStyles = styleSheet;
